import {
    ApplicationConfig,
    EnvironmentProviders,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners, Provider,
    Type
} from '@angular/core';
import {
    AuthConfig,
    AuthModule,
    ConfigurationModule,
    createCombinedTranslateLoader,
    DataClientVersion,
    ErrorModule,
    ErrorService,
    FedeltaHttpModule,
    I18nService,
    ImageService,
    LocalConfigurationService,
    provideEntityModel,
    provideSearchModel,
    ResourceModule,
    ServerLoggingService,
    ValidationModule,
    VersionService
} from '@fedelta-pos/data-client';
import {StoreDetailsPageComponent} from './store/store-details-page/store-details-page.component';
import {STORE_SEARCH_FILTER_PROVIDER, StoreSearchModel} from './store/store-search.model';
import {
    OrderTimeSelectionPageComponent
} from './ordering-context/order-time-selection-page/order-time-selection-page.component';
import {
    OrderingContextEditPageComponent
} from './ordering-context/ordering-context-edit-page/ordering-context-edit-page.component';
import {
    OrderingContextSelectionPageComponent
} from './ordering-context/ordering-context-selection-page/ordering-context-selection-page.component';
import {
    OrderingContextModel,
    OrderingContextStoreSearchFilterProvider
} from './ordering-context/ordering-context.model';
import {ActiveSchedulesSearchModel} from './schedule/active-schedules-search.model';
import {SessionModel} from './session/session.model';
import {AnalyticsService} from './logging/analytics.service';
import {ActorType, ConfigurationModel} from './configuration/configuration.model';
import {AppEntityModel} from './configuration/app-entity.model';
import {WebOrderingAppVersion} from './configuration/version';
import {Adyen3dsPageComponent} from './payment/adyen-3ds-page/adyen-3ds-page.component';
import {OrderPaymentDetailsEntityModel} from './payment/order-payment-details-entity.model';
import {PaymentPageComponent} from './payment/payment-page/payment-page.component';
import {PaymentModel} from './payment/payment.model';
import {AuthorisedPaymentEntityModel} from './payment/tap-to-pay/authorised-payment-entity.model';
import {TapToPayService} from './payment/tap-to-pay/tap-to-pay.service';
import {TipSelectionPageComponent} from './payment/tip-selection-page/tip-selection-page.component';
import {WalletPaymentPageComponent} from './payment/wallet-payment/wallet-payment-page/wallet-payment-page.component';
import {BeaconSearchModel} from './beacon/beacon-search.model';
import {DealCodeEntryPageComponent} from './deal/deal-code-entry-page/deal-code-entry-page.component';
import {DealEntityModel} from './deal/deal-entity.model';
import {DealModel} from './deal/deal.model';
import {DealsPageComponent} from './deal/deals-page/deals-page.component';
import {ChangePasswordPageComponent} from './identity/change-password-page/change-password-page.component';
import {IdentificationPageComponent} from './identity/identification-page/identification-page.component';
import {IdentityModel} from './identity/identity.model';
import {MemberSummaryEntityModel} from './identity/member-summary-entity.model';
import {ProfileEditPageComponent} from './identity/profile-edit-page/profile-edit-page.component';
import {WaiterNameEditPageComponent} from './identity/waiter-name-edit-page/waiter-name-edit-page.component';
import {MenuEntityModel} from './menu/menu-entity.model';
import {MenuSearchPageComponent} from './menu/menu-search-page/menu-search-page.component';
import {MenuModel} from './menu/menu.model';
import {SellableSizesSearchModel} from './menu/sellable-sizes-search.model';
import {GroupOrderPageComponent} from './group-order/group-order-page/group-order-page.component';
import {NameEditPageComponent} from './group-order/name-edit-page/name-edit-page.component';
import {OrderTransferTargetEntityModel} from './group-order/order-transfer-target-entity.model';
import {OrderTransferTargetSearchModel} from './group-order/order-transfer-target-search.model';
import {AppDeviceStatusPageComponent} from './app-device/app-device-status-page/app-device-status-page.component';
import {AppDeviceService} from './app-device/app-device.service';
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {
    provideMissingTranslationHandler,
    provideTranslateParser,
    provideTranslateService,
    TranslateLoader
} from '@ngx-translate/core';
import {OrderConfirmationPageComponent} from './order/order-confirmation-page/order-confirmation-page.component';
import {OrderDetailsPageComponent} from './order/order-details-page/order-details-page.component';
import {OrderItemEditPageComponent} from './order/order-item/order-item-edit-page/order-item-edit-page.component';
import {
    OrderItemCombinedExtrasEditPageComponent
} from './order/order-item/order-item-options-panel/order-item-combined-extras-edit-page/order-item-combined-extras-edit-page.component';
import {
    OrderItemExtraSizesEditPageComponent
} from './order/order-item/order-item-options-panel/order-item-extra-sizes-edit-page/order-item-extra-sizes-edit-page.component';
import {
    OrderItemPlgEditPageComponent
} from './order/order-item/order-item-options-panel/order-item-plg-edit-page/order-item-plg-edit-page.component';
import {OrderNotesEditPageComponent} from './order/order-notes-edit-page/order-notes-edit-page.component';
import {OrderModel} from './order/order.model';
import {PromoPageComponent} from './promo/promo-page/promo-page.component';
import {PromoModel} from './promo/promo.model';
import {SimpleDialogPageComponent} from './notification/simple-dialog-page/simple-dialog-page.component';
import {CustomerOrdersPageComponent} from './customer-info/customer-orders-page/customer-orders-page.component';
import {FeedbackPageComponent} from './customer-info/feedback-page/feedback-page.component';
import {InvoiceRequestPageComponent} from './customer-info/invoice-request-page/invoice-request-page.component';
import {MarketingEntryPageComponent} from './customer-info/marketing-entry-page/marketing-entry-page.component';
import {PhoneEditPageComponent} from './customer-info/phone-edit-page/phone-edit-page.component';
import {SaleReceiptPageComponent} from './customer-info/sale-receipt-page/sale-receipt-page.component';
import {EntryPageComponent} from './start/entry-page/entry-page.component';
import {LegalPageComponent} from './legal/legal-page/legal-page.component';
import {LoyaltyCardsPageComponent} from './loyalty-card/loyalty-cards-page/loyalty-cards-page.component';
import {NavMenuPageComponent} from './nav-menu/nav-menu-page.component';
import {OptionsPageComponent} from './inputs/selection-field/options-page/options-page.component';
import {OrderingPageComponent} from './ordering-page/ordering-page.component';
import {ReorderPageComponent} from './ordering-page/reorder-page/reorder-page.component';
import {QrCodePageComponent} from './ui-elements/qr-code/qr-code-page/qr-code-page.component';
import {NavigationService} from './navigation/navigation.service';
import {Page} from './navigation/page';
import {AppDeviceEntityModel} from './app-device/app-device-entity-model.service';
import {environment} from '../environments/environment';
import {firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {DeferredInjectionService} from './deferred-injection.service';
import {RsaVerificationPageComponent} from './order/rsa-verification-page/rsa-verification-page.component';
import {RsaBlockPageComponent} from './menu/rsa-block-page/rsa-block-page.component';

export function authConfigFactory(): AuthConfig {
    return {
        httpTimeout: 10000,
        getDeviceId: () => firstValueFrom(ConfigurationModel.deviceId$),
        getActorType: () => firstValueFrom(ConfigurationModel.actorType$.pipe(
            map((v: ActorType) => v.toString())
        ))
    };
}

const PAGES: Type<Page>[] = [
    Adyen3dsPageComponent,
    ChangePasswordPageComponent,
    CustomerOrdersPageComponent,
    DealCodeEntryPageComponent,
    DealsPageComponent,
    EntryPageComponent,
    FeedbackPageComponent,
    IdentificationPageComponent,
    InvoiceRequestPageComponent,
    LegalPageComponent,
    LoyaltyCardsPageComponent,
    MarketingEntryPageComponent,
    OrderNotesEditPageComponent,
    ProfileEditPageComponent,
    MenuSearchPageComponent,
    NameEditPageComponent,
    NavMenuPageComponent,
    OptionsPageComponent,
    OrderConfirmationPageComponent,
    OrderDetailsPageComponent,
    OrderingPageComponent,
    OrderItemCombinedExtrasEditPageComponent,
    OrderItemEditPageComponent,
    OrderItemExtraSizesEditPageComponent,
    OrderItemPlgEditPageComponent,
    OrderTimeSelectionPageComponent,
    OrderingContextEditPageComponent,
    OrderingContextSelectionPageComponent,
    PaymentPageComponent,
    PhoneEditPageComponent,
    PromoPageComponent,
    QrCodePageComponent,
    ReorderPageComponent,
    RsaBlockPageComponent,
    RsaVerificationPageComponent,
    SaleReceiptPageComponent,
    AppDeviceStatusPageComponent,
    GroupOrderPageComponent,
    SimpleDialogPageComponent,
    StoreDetailsPageComponent,
    TipSelectionPageComponent,
    WaiterNameEditPageComponent,
    WalletPaymentPageComponent
];

const MODULE_PROVIDERS = [
    importProvidersFrom(AuthModule.forRootWithFactoryProvider({
        provide: AuthConfig,
        useFactory: authConfigFactory
    })),
    importProvidersFrom(FedeltaHttpModule.forRoot({
        httpTimeout: 20000
    })),
    importProvidersFrom(ConfigurationModule.forRoot({
        application: "woa",
        localConfigFile: environment.production
            ? document.querySelector('meta[name="appconfig-url"]')?.getAttribute('content')
            : "assets/appconfig.development.json",
        contextNames: ["userId", "webTerminalId", "outletId", "application"]
    })),
    importProvidersFrom(ErrorModule.forRoot()),
    importProvidersFrom(ValidationModule.forRoot({
        application: "woa"
    })),
    importProvidersFrom(ResourceModule.forRoot({
        nameSpace: "wo",
        entityNames: [
            "app",
            "identity",
            "menu",
            "device-token",
            "deal",
            "member-summary",
            "app-order-payment-details",
            "app-order-transfer-target",
            "app-device",
            "authorised-payment"
        ],
        searchNames: [
            "identity",
            "member-sales",
            "sellable-sizes",
            "store",
            "beacon",
            "active-schedules",
            "app-order-transfer-target"
        ],
        actionNames: [
            "active-deal-validation",
            "app-usage-notification",
            "app-feedback",
            "identity-change-password",
            "identity-request-password-reset",
            "identity-reset-password",
            "available-order-times",
            "loyalty-card-redeem",
            "integrated-delivery-address-validation",
            "invoice-email-request",
            "app-device-registration",
            "stripe-connection-token-refresh",
            "tap-to-pay-intent-creation",
            "app-order-initiation",
            "app-order-transfer",
            "app-order-transfer-request-poll",
            "app-order-placement-status",
            "app-order-placement",
            "app-order-postscript",
            "app-pending-orders",
            "app-device-login",
            "app-device-logout",
            "table-status-manage-rsa"
        ]
    }))
];

const GLOBAL_ENTITY_PROVIDERS: Provider[] = [
    provideEntityModel(AppEntityModel),
    provideEntityModel(AuthorisedPaymentEntityModel),
    provideEntityModel(DealEntityModel),
    provideEntityModel(MemberSummaryEntityModel),
    provideEntityModel(MenuEntityModel),
    provideEntityModel(OrderPaymentDetailsEntityModel),
    provideEntityModel(OrderTransferTargetEntityModel),
    provideEntityModel(AppDeviceEntityModel)
];

const GLOBAL_SEARCH_PROVIDERS: Provider[] = [
    provideSearchModel(ActiveSchedulesSearchModel),
    provideSearchModel(BeaconSearchModel),
    provideSearchModel(OrderTransferTargetSearchModel),
    provideSearchModel(SellableSizesSearchModel),
    provideSearchModel(StoreSearchModel)
];

const DATA_CLIENT_SERVICES: Provider[] = [
    ErrorService,
    ImageService,
    ServerLoggingService,
    VersionService,
    DataClientVersion
];

const APP_INITIALIZERS: EnvironmentProviders[] = [
    provideAppInitializer(() => {
        const configurationService: LocalConfigurationService = inject(LocalConfigurationService);
        const deferredInjectionService: DeferredInjectionService = inject(DeferredInjectionService);

        // Inject services/models and register for deferred injection
        const configurationModel: ConfigurationModel = deferredInjectionService.inject(ConfigurationModel);
        const navigationService: NavigationService = deferredInjectionService.inject(NavigationService);
        deferredInjectionService.injectAll(
            AppDeviceService,
            TapToPayService,
            AnalyticsService,
            SessionModel,
            OrderModel,
            MenuModel,
            PaymentModel,
            PromoModel,
            DealModel,
            IdentityModel,
            OrderingContextModel,
            STORE_SEARCH_FILTER_PROVIDER
        );
        deferredInjectionService.injectDeferred();

        // Initialize services
        console.log("pathname = " + window.location.pathname);
        const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
        if (urlParams.get("localConfigFile")) {
            configurationService.setLocalConfigFile("assets/" + urlParams.get("localConfigFile"));
        }
        navigationService.registerComponentTypes(PAGES);
        return configurationModel.init();
    })
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideHttpClient(withInterceptorsFromDi()),

        I18nService,
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: createCombinedTranslateLoader,
                deps: [HttpClient, I18nService]
            },
            parser: provideTranslateParser(I18nService),
            missingTranslationHandler: provideMissingTranslationHandler(I18nService)
        }),

        ...MODULE_PROVIDERS,
        ...GLOBAL_ENTITY_PROVIDERS,
        ...GLOBAL_SEARCH_PROVIDERS,
        {
            provide: STORE_SEARCH_FILTER_PROVIDER,
            useClass: OrderingContextStoreSearchFilterProvider
        },
        ...DATA_CLIENT_SERVICES,

        Geolocation,

        ...APP_INITIALIZERS,

        WebOrderingAppVersion
    ]
};
